import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { GetSalesInsightsAggregatedRequest, SalesInsights, SalesInsightsCompareResponse, useSalesInsightsCompare } from '../../../../api/indicators';
import { DashboardFiltersContext } from '../filters/DashboardFiltersContext';
import { dateRange, formatDateTimeParam } from '../../../../helpers/dateUtils';
import { DashboardContext } from '../../DashboardContext';
import { calculateCombinedPercentage, calculateDifferencePercentage } from '../../dashboardHelpers';
import NumericChartCompared from '../charts/numericChart/NumericChartCompared';


const ComparedSalesInsightAreaRetail = () => {
    const { t } = useTranslation();
    const { filters, dateRangeConfig } = useContext(DashboardFiltersContext);
    const { companyConfig, refreshDate } = useContext(DashboardContext);

    const [params, setParams] = useState<GetSalesInsightsAggregatedRequest>({
        fields: 'transactionVolume,netSales,totalTax,avgTransactionValue,activeDevices,activeStaff,totalDiscount,activeCustomers,totalQuantity,totalServiceCharge,totalCustomerDiscount,totalItemDiscount,totalBasketDiscount,totalContributions,totalSales,totalAmountPaidByPoints,refunds,netMargin',
        startDateTime: formatDateTimeParam(filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
        endDateTime: formatDateTimeParam(filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate),
        compareStartDateTime: formatDateTimeParam(filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate),
        compareEndDateTime: formatDateTimeParam(filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate),
        locations: filters?.locations?.join(',') ? filters?.locations?.join(',') : companyConfig?.locations?.map(location => location.id).join(','),
        locationArea: filters?.locationArea?.id
    });

    const {
        data: comparedSalesInsights,
        isLoading,
        isError,
        refetch: fetchSalesInsights
    } = useSalesInsightsCompare<SalesInsightsCompareResponse>(params, 'compared-sales-insights');

    const getValues = useCallback((key: keyof SalesInsights): [number, number] => [
        comparedSalesInsights?.salesInsights?.[key] ?? 0,
        comparedSalesInsights?.comparedSalesInsights?.[key] ?? 0
      ], [comparedSalesInsights]);
      
      const [totalSalesIncTax, totalSalesIncTaxCompared] = getValues('totalSalesIncTax');
      const { totalSalesIncPercent, totalSalesIncPercentCompared } = useMemo(() => ({
        totalSalesIncPercent: calculateDifferencePercentage(totalSalesIncTaxCompared, totalSalesIncTax),
        totalSalesIncPercentCompared: calculateDifferencePercentage(totalSalesIncTax, totalSalesIncTaxCompared)
      }), [totalSalesIncTax, totalSalesIncTaxCompared]);
      
      const [netSalesIncTax, netSalesIncTaxCompared] = getValues('netSalesIncTax');
      const { netSalesIncPercent, netSalesIncPercentCompared } = useMemo(() => ({
        netSalesIncPercent: calculateDifferencePercentage(netSalesIncTaxCompared, netSalesIncTax),
        netSalesIncPercentCompared: calculateDifferencePercentage(netSalesIncTax, netSalesIncTaxCompared)
      }), [netSalesIncTax, netSalesIncTaxCompared]);
      
      const [transactionVolume, transactionVolumeCompared] = getValues('transactionVolume');
      const { transactionVolumePercent, transactionVolumePercentCompared } = useMemo(() => ({
        transactionVolumePercent: calculateDifferencePercentage(transactionVolumeCompared, transactionVolume),
        transactionVolumePercentCompared: calculateDifferencePercentage(transactionVolume, transactionVolumeCompared)
      }), [transactionVolume, transactionVolumeCompared]);
      
      const [netMargin, netMarginCompared] = getValues('netMargin');
      const { netMarginPercent, netMarginPercentCompared } = useMemo(() => ({
        netMarginPercent: calculateDifferencePercentage(netMarginCompared, netMargin),
        netMarginPercentCompared: calculateDifferencePercentage(netMargin, netMarginCompared)
      }), [netMargin, netMarginCompared]);
      
          
      const [totalCustomerDiscount, totalCustomerDiscountCompared] = getValues('totalCustomerDiscount');
      const { totalCustomerDiscountPercent, totalCustomerDiscountPercentCompared } = useMemo(() => {
        const { currentPercent, comparedPercent } = calculateCombinedPercentage(totalCustomerDiscount, totalCustomerDiscountCompared);
        return {
          totalCustomerDiscountPercent: currentPercent,
          totalCustomerDiscountPercentCompared: comparedPercent
        };
      }, [totalCustomerDiscount, totalCustomerDiscountCompared]);
      

      const { totalDiscountPercent, totalDiscountPercentCompared } = useMemo(() => ({
        totalDiscountPercent: calculateDifferencePercentage(comparedSalesInsights?.comparedSalesInsights.totalDiscount, comparedSalesInsights?.salesInsights.totalDiscount),
        totalDiscountPercentCompared: calculateDifferencePercentage(comparedSalesInsights?.salesInsights.totalDiscount, comparedSalesInsights?.comparedSalesInsights.totalDiscount)
      }), [comparedSalesInsights?.salesInsights.totalDiscount, comparedSalesInsights?.comparedSalesInsights.totalDiscount]);

    useEffect(() => {
        if (refreshDate != null) {
            fetchSalesInsights();
        }
    }, [refreshDate]);

    useEffect(() => {
        setParams({
            ...params,
            startDateTime: filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate,
            endDateTime: filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate,
            compareStartDateTime: formatDateTimeParam(filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate),
            compareEndDateTime: formatDateTimeParam(filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate),
            locations: filters?.locations?.join(',') ? filters?.locations?.join(',') : companyConfig?.locations?.map(location => location.id).join(','),
            locationArea: filters?.locationArea?.id
        });
    }, [filters]);

    useEffect(() => { fetchSalesInsights(); }, [params]);

    return (
        <Box sx={{ marginY: '20px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', marginBottom: '20px' }}>
            <NumericChartCompared
              title={t("screens.dashboarding.charts.sales")}
              subtitle={t("screens.dashboarding.charts.includingTax")}
              currency={comparedSalesInsights?.currency}
              currentDateFrom={filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate}
                currentDateTo={filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate}
                comparedDateFrom={filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate}
                comparedDateTo={filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate} 
              data={comparedSalesInsights?.salesInsights.totalSalesIncTax}
              dataCompared={comparedSalesInsights?.comparedSalesInsights.totalSalesIncTax}
              percent={totalSalesIncPercent}
              percentCompared={totalSalesIncPercentCompared}
              isLoading={isLoading}
              isError={isError}
              linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesBreakdown.aspx`}
              buttonText={t("screens.dashboarding.charts.salesOverview")}
            />
            <NumericChartCompared
              title={t("screens.dashboarding.charts.transactions")}
              subtitle={t("screens.dashboarding.charts.volume")}
              currentDateFrom={filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate}
                currentDateTo={filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate}
                comparedDateFrom={filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate}
                comparedDateTo={filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate} 
              data={comparedSalesInsights?.salesInsights.transactionVolume}
              dataCompared={comparedSalesInsights?.comparedSalesInsights.transactionVolume}
              percent={transactionVolumePercent}
              percentCompared={transactionVolumePercentCompared}
              isLoading={isLoading}
              isError={isError}
              linkChart={`${process.env.REACT_APP_OAUTH_CALLBACK_URL}transactions`}
              buttonText={t("screens.dashboarding.charts.transactionsOverview")}
            />
            <NumericChartCompared
              title={t("screens.dashboarding.charts.netSales")}
              subtitle={t("screens.dashboarding.charts.includingTax")}
              currency={comparedSalesInsights?.currency}
              currentDateFrom={filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate}
                currentDateTo={filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate}
                comparedDateFrom={filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate}
                comparedDateTo={filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate} 
              data={comparedSalesInsights?.salesInsights.netSalesIncTax}
              dataCompared={comparedSalesInsights?.comparedSalesInsights.totalSalesIncTax}
              percent={netSalesIncPercent}
              percentCompared={netSalesIncPercentCompared}
              isLoading={isLoading}
              isError={isError}
              linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesBreakdown.aspx`}
              buttonText={t("screens.dashboarding.charts.salesOverview")}
            />
            <NumericChartCompared
              title={t("screens.dashboarding.charts.netMargin")}
              subtitle={t("screens.dashboarding.charts.excludingTax")}
              currency={comparedSalesInsights?.currency}
              currentDateFrom={filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate}
                currentDateTo={filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate}
                comparedDateFrom={filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate}
                comparedDateTo={filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate} 
              data={comparedSalesInsights?.salesInsights.netMargin}
              dataCompared={comparedSalesInsights?.comparedSalesInsights.netMargin}
              percent={netMarginPercent}
              percentCompared={netMarginPercentCompared}
              isLoading={isLoading}
              isError={isError}
              linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesBreakdown.aspx`}
              buttonText={t("screens.dashboarding.charts.salesOverview")}
            />
          </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <NumericChartCompared
              title={t("screens.dashboarding.charts.discounts")}
              subtitle={t("screens.dashboarding.charts.includingTax")}
              currency={comparedSalesInsights?.currency}
              currentDateFrom={filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate}
                currentDateTo={filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate}
                comparedDateFrom={filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate}
                comparedDateTo={filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate} 
              data={comparedSalesInsights?.salesInsights.totalDiscount}
              dataCompared={comparedSalesInsights?.comparedSalesInsights.totalDiscount}
              percent={totalDiscountPercent}
              percentCompared={totalDiscountPercentCompared}
              isLoading={isLoading}
              isError={isError}
              linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/PromotionReport.aspx`}
              buttonText={t("screens.dashboarding.charts.promotionsReport")}
            />
            <NumericChartCompared
              title={t("screens.dashboarding.charts.discounts")}
              subtitle={t("screens.dashboarding.charts.customerDiscount")}
              currentDateFrom={filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate}
                currentDateTo={filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate}
                comparedDateFrom={filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate}
                comparedDateTo={filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate} 
              data={comparedSalesInsights?.salesInsights.totalCustomerDiscount}
              dataCompared={comparedSalesInsights?.comparedSalesInsights.totalCustomerDiscount}
              percent={totalCustomerDiscountPercent}
              percentCompared={totalCustomerDiscountPercentCompared}
              isLoading={isLoading}
              isError={isError}
            />
            </Box>
        </Box>
    )
}

export default ComparedSalesInsightAreaRetail;
